import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, Search, BookOpen, Clock, AlertTriangle, 
  FileText, Download, User, Calendar, Printer, Edit,
  MessageSquare, Phone, Check, Mail
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const PracticeLibraryPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { practice, user } = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [filteredProcedures, setFilteredProcedures] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // SMS state
  const [patients, setPatients] = useState([]);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsProcedure, setSmsProcedure] = useState(null);
  const [smsPatientSearchQuery, setSmsPatientSearchQuery] = useState('');
  const [filteredSmsPatients, setFilteredSmsPatients] = useState([]);
  const [smsPatient, setSmsPatient] = useState(null);
  const [sendingSMS, setSendingSMS] = useState(false);

  // Email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailProcedure, setEmailProcedure] = useState(null);
  const [emailPatientSearchQuery, setEmailPatientSearchQuery] = useState('');
  const [filteredEmailPatients, setFilteredEmailPatients] = useState([]);
  const [emailPatient, setEmailPatient] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchProcedures();
    loadPatients();
  }, []);

  // Filter patients for SMS modal - only show patients with phone numbers
  useEffect(() => {
    let filtered = [...patients];
    filtered = filtered.filter(patient => patient.cellPhone || patient.phone);

    if (smsPatientSearchQuery.trim()) {
      const searchLower = smsPatientSearchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
        (patient.email && patient.email.toLowerCase().includes(searchLower))
      );
    }

    filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });

    setFilteredSmsPatients(filtered);
  }, [smsPatientSearchQuery, patients]);

  // Filter patients for Email modal: must have an email address
  useEffect(() => {
    let filtered = [...patients];
    filtered = filtered.filter(patient => patient.email);

    if (emailPatientSearchQuery.trim()) {
      const searchLower = emailPatientSearchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
        (patient.email && patient.email.toLowerCase().includes(searchLower))
      );
    }

    filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });

    setFilteredEmailPatients(filtered);
  }, [emailPatientSearchQuery, patients]);

  const loadPatients = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${backendUrl}/api/practice/patients`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPatients(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load patients:', error);
    }
  };

  useEffect(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    
    if (!searchLower) {
      // Even without search, ensure alphabetical order
      const sorted = [...procedures].sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setFilteredProcedures(sorted);
      return;
    }
    
    // Alternative terms mapping (same as backend)
    const alternatives = {
      'zirconium': 'zirconia',
      'zircon': 'zirconia',
      'all-on-x': 'all on x',
      'allonx': 'all on x',
      'all-on-4': 'all on x',
      'all-on-6': 'all on x',
      'deep cleaning': 'scaling',
      'wisdom tooth': 'wisdom',
      'wisdom teeth': 'wisdom',
      'third molar': 'wisdom',
      'root canal': 'root canal',
      'rct': 'root canal',
      'implant': 'implant',
      'extraction': 'extraction',
      'filling': 'filling'
    };
    
    // Get search terms
    const searchTerms = [searchLower];
    if (alternatives[searchLower]) {
      searchTerms.push(alternatives[searchLower]);
    }
    
    const filtered = procedures.filter(procedure => {
      const name = procedure.name.toLowerCase();
      const specialtyName = (procedure.specialtyName || '').toLowerCase();
      const overview = (procedure.overview || '').toLowerCase();
      
      // Check each search term
      for (const term of searchTerms) {
        // Simple substring match
        if (name.includes(term) || specialtyName.includes(term) || overview.includes(term)) {
          return true;
        }
        
        // For multi-word terms, check if all words are present in name
        const words = term.split(' ');
        if (words.length > 1) {
          if (words.every(word => name.includes(word))) {
            return true;
          }
        }
      }
      
      return false;
    });
    
    // Sort by relevance (exact name matches first)
    filtered.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      
      // Exact matches first
      if (aName.includes(searchLower) && !bName.includes(searchLower)) return -1;
      if (!aName.includes(searchLower) && bName.includes(searchLower)) return 1;
      
      // Alphabetical
      return aName.localeCompare(bName);
    });
    
    setFilteredProcedures(filtered);
  }, [searchQuery, procedures]);

  // Transform Global Procedure format to display format
  const transformProcedure = (procedure) => {
    // Check if this is a Global Procedure (has first24Hours, painAndSwelling, etc.)
    const isGlobalProcedure = procedure.first24Hours || procedure.painAndSwelling || procedure.oralHygiene || procedure.purpose;
    
    console.log(`🔄 Transform check for "${procedure.name}": isGlobal=${isGlobalProcedure}, hasOverview=${!!procedure.overview}, hasPurpose=${!!procedure.purpose}, hasFirst24=${!!procedure.first24Hours}`);
    
    if (!isGlobalProcedure) {
      return procedure; // Legacy format, return as-is
    }
    
    // Build overview from all Global Procedure fields.
    // We only count it as a real "Global Procedure" if it has at least one
    // structured section field with actual content. (The section fields can
    // exist as empty arrays — and empty arrays are truthy in JS — so we
    // explicitly check for length.)
    const _hasItems = (v) => {
      if (!v) return false;
      if (Array.isArray(v)) return v.some(item => item && String(item).trim());
      if (typeof v === 'string') return v.trim().length > 0;
      return true;
    };
    const hasStructuredSections =
      _hasItems(procedure.first24Hours) ||
      _hasItems(procedure.painAndSwelling) ||
      _hasItems(procedure.oralHygiene) ||
      _hasItems(procedure.diet) ||
      _hasItems(procedure.activity) ||
      _hasItems(procedure.bleeding) ||
      _hasItems(procedure.followUp) ||
      _hasItems(procedure.specialPrecautions);

    if (!hasStructuredSections) {
      // Legacy / PDF-imported shape — trust whatever the backend gave us in
      // overview (which is built from fullText when needed).
      return {
        ...procedure,
        overview: procedure.overview || procedure.fullText || 'No content available',
        isGlobalProcedure: false,
      };
    }

    let overviewParts = [];
    
    if (procedure.purpose) {
      overviewParts.push(`Purpose:\n${procedure.purpose}`);
    }
    
    if (procedure.first24Hours && procedure.first24Hours.length > 0) {
      const items = Array.isArray(procedure.first24Hours) ? procedure.first24Hours : [procedure.first24Hours];
      overviewParts.push(`\nFirst 24 Hours:\n${items.map(item => `• ${item}`).join('\n')}`);
    }
    
    if (procedure.painAndSwelling && procedure.painAndSwelling.length > 0) {
      const items = Array.isArray(procedure.painAndSwelling) ? procedure.painAndSwelling : [procedure.painAndSwelling];
      overviewParts.push(`\nPain and Swelling:\n${items.map(item => `• ${item}`).join('\n')}`);
    }
    
    if (procedure.oralHygiene && procedure.oralHygiene.length > 0) {
      const items = Array.isArray(procedure.oralHygiene) ? procedure.oralHygiene : [procedure.oralHygiene];
      overviewParts.push(`\nOral Hygiene:\n${items.map(item => `• ${item}`).join('\n')}`);
    }
    
    if (procedure.diet && procedure.diet.length > 0) {
      const items = Array.isArray(procedure.diet) ? procedure.diet : [procedure.diet];
      overviewParts.push(`\nDiet:\n${items.map(item => `• ${item}`).join('\n')}`);
    }
    
    if (procedure.activity && procedure.activity.length > 0) {
      const items = Array.isArray(procedure.activity) ? procedure.activity : [procedure.activity];
      overviewParts.push(`\nActivity:\n${items.map(item => `• ${item}`).join('\n')}`);
    }
    
    if (procedure.specialPrecautions && procedure.specialPrecautions.length > 0) {
      const items = Array.isArray(procedure.specialPrecautions) ? procedure.specialPrecautions : [procedure.specialPrecautions];
      overviewParts.push(`\nSpecial Precautions:\n${items.map(item => `• ${item}`).join('\n')}`);
    }
    
    if (procedure.followUp && procedure.followUp.length > 0) {
      const items = Array.isArray(procedure.followUp) ? procedure.followUp : [procedure.followUp];
      overviewParts.push(`\nFollow Up:\n${items.map(item => `• ${item}`).join('\n')}`);
    }
    
    const transformedOverview = overviewParts.join('\n') || procedure.overview || 'No content available';
    console.log(`✅ Transformed "${procedure.name}" - overview length: ${transformedOverview.length}`);
    
    return {
      ...procedure,
      overview: transformedOverview,
      isGlobalProcedure: true
    };
  };

  const fetchProcedures = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching procedures...');
      // Use practice-specific procedures endpoint with authentication
      const result = await practiceApi.getPracticeProcedures();
      console.log('🔍 API Response:', result);
      
      // Practice endpoint returns procedures in different formats
      const data = result.procedures || result.data || result;
      console.log('🔍 Procedures count:', data?.length);
      
      // Log first procedure to see its structure
      if (data && data.length > 0) {
        console.log('🔍 First procedure fields:', Object.keys(data[0]));
        console.log('🔍 First procedure sample:', JSON.stringify(data[0], null, 2).substring(0, 500));
      }
      
      // Transform Global Procedures to have overview field populated
      const transformedData = data.map(transformProcedure);
      
      // Sort alphabetically by name
      const sortedData = [...transformedData].sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      
      setProcedures(sortedData);
      setFilteredProcedures(sortedData);
      console.log('🔍 Procedures loaded successfully');
    } catch (error) {
      console.error('❌ Fetch procedures error:', error);
      toast({
        title: "Error",
        description: "Failed to load procedures",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProcedureSelect = (procedure) => {
    setSelectedProcedure(procedure);
    setShowPreview(true);
  };

  const handleEditProcedure = (procedure) => {
    console.log('🔧 Navigating to edit procedure:', procedure.name);
    // Navigate to edit page for practice-specific customization
    navigate(`/practice/procedures/${procedure.id}/edit`);
  };

  const handleDownloadPDF = async (procedure) => {
    console.log('🎯 Starting PDF download for procedure:', procedure?.name);
    
    try {
      if (!procedure) {
        console.error('No procedure selected for PDF generation');
        toast({
          title: "Error", 
          description: "No procedure selected for PDF generation",
          variant: "destructive",
        });
        return;
      }

      // Show loading toast
      toast({
        title: "Creating PDF",
        description: "Generating PDF document...",
      });

      // Use new direct PDF download endpoint
      const response = await practiceApi.downloadPDF({
        procedureId: procedure.id,
        procedureName: procedure.name
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create safe filename
      const safeName = procedure.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `${safeName}_instructions.pdf`;
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: `PDF downloaded: ${procedure.name}`,
      });
      
      console.log('📄 PDF download successful for:', procedure.name);
    } catch (error) {
      console.error('❌ PDF download error:', error);
      toast({
        title: "Error",
        description: `Failed to download PDF: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    }
  };

  const handlePrintPDF = async (procedure) => {
    console.log('🖨️ Starting print for procedure:', procedure?.name);
    console.log('🖨️ Procedure data:', JSON.stringify(procedure, null, 2));
    
    try {
      if (!procedure) {
        console.error('No procedure selected for printing');
        toast({
          title: "Error", 
          description: "No procedure selected for printing",
          variant: "destructive",
        });
        return;
      }

      // Use Quick Print with HTML - much faster and more reliable
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast({
          title: "Popup Blocked",
          description: "Please allow popups to print.",
          variant: "destructive",
        });
        return;
      }

      const practiceName = practice?.name || 'Dental Practice';
      const practicePhone = practice?.phone || '';
      const practiceWebsite = practice?.website || '';
      const practiceLogo = practice?.logoUrl || practice?.branding?.logo || '';
      
      // Build address string
      let practiceAddress = '';
      if (practice?.address) {
        const addr = practice.address;
        const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter(p => p);
        practiceAddress = parts.join(', ');
      }
      
      const procedureName = procedure.name || 'Procedure Instructions';
      
      // Build content - handle BOTH legacy format (overview) AND Global Procedure format (purpose, first24Hours, etc.)
      let content = '';
      
      // Check if this is a Global Procedure (has structured fields)
      const isGlobalProcedure = procedure.purpose || procedure.first24Hours || procedure.painAndSwelling || procedure.oralHygiene;
      
      if (isGlobalProcedure) {
        console.log('🖨️ Detected Global Procedure format - building content from structured fields');
        let parts = [];
        
        if (procedure.purpose) {
          parts.push(`<strong>Purpose:</strong>\n${procedure.purpose}`);
        }
        
        if (procedure.first24Hours && procedure.first24Hours.length > 0) {
          const items = Array.isArray(procedure.first24Hours) ? procedure.first24Hours : [procedure.first24Hours];
          parts.push(`\n<strong>First 24 Hours:</strong>\n${items.filter(i => i).map(item => `• ${item}`).join('\n')}`);
        }
        
        if (procedure.painAndSwelling && procedure.painAndSwelling.length > 0) {
          const items = Array.isArray(procedure.painAndSwelling) ? procedure.painAndSwelling : [procedure.painAndSwelling];
          parts.push(`\n<strong>Pain and Swelling:</strong>\n${items.filter(i => i).map(item => `• ${item}`).join('\n')}`);
        }
        
        if (procedure.oralHygiene && procedure.oralHygiene.length > 0) {
          const items = Array.isArray(procedure.oralHygiene) ? procedure.oralHygiene : [procedure.oralHygiene];
          parts.push(`\n<strong>Oral Hygiene:</strong>\n${items.filter(i => i).map(item => `• ${item}`).join('\n')}`);
        }
        
        if (procedure.diet && procedure.diet.length > 0) {
          const items = Array.isArray(procedure.diet) ? procedure.diet : [procedure.diet];
          parts.push(`\n<strong>Diet:</strong>\n${items.filter(i => i).map(item => `• ${item}`).join('\n')}`);
        }
        
        if (procedure.activity && procedure.activity.length > 0) {
          const items = Array.isArray(procedure.activity) ? procedure.activity : [procedure.activity];
          parts.push(`\n<strong>Activity:</strong>\n${items.filter(i => i).map(item => `• ${item}`).join('\n')}`);
        }
        
        if (procedure.specialPrecautions && procedure.specialPrecautions.length > 0) {
          const items = Array.isArray(procedure.specialPrecautions) ? procedure.specialPrecautions : [procedure.specialPrecautions];
          parts.push(`\n<strong>Special Precautions:</strong>\n${items.filter(i => i).map(item => `• ${item}`).join('\n')}`);
        }
        
        if (procedure.followUp && procedure.followUp.length > 0) {
          const items = Array.isArray(procedure.followUp) ? procedure.followUp : [procedure.followUp];
          parts.push(`\n<strong>Follow Up:</strong>\n${items.filter(i => i).map(item => `• ${item}`).join('\n')}`);
        }
        
        content = parts.join('\n') || 'No content available';
      } else {
        // Legacy format - use overview or fullText
        content = procedure.overview || procedure.fullText || 'No content available';
      }
      
      console.log('🖨️ Final content length:', content.length);

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${procedureName}</title>
          <style>
            * {
              box-sizing: border-box !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            body {
              font-family: Arial, sans-serif !important;
              font-size: 12pt !important;
              line-height: 1.55 !important;
              margin: 0.6in !important;
              padding: 0 !important;
              color: #1f2937 !important;
            }
            .header {
              text-align: center !important;
              margin-bottom: 18px !important;
              border-bottom: 2px solid #0b2545 !important;
              padding-bottom: 12px !important;
            }
            .header h1 {
              font-size: 18pt !important;
              font-weight: bold !important;
              color: #0b2545 !important;
              margin: 0 0 6px 0 !important;
              padding: 0 !important;
            }
            .header p {
              font-size: 11pt !important;
              margin: 3px 0 !important;
              color: #475569 !important;
            }
            .procedure-title {
              font-size: 18pt !important;
              font-weight: bold !important;
              color: #0b2545 !important;
              text-align: center !important;
              margin: 18px auto 14px !important;
              padding: 0 !important;
              line-height: 1.2 !important;
            }
            .content {
              font-size: 12pt !important;
              line-height: 1.55 !important;
              margin-top: 6px !important;
              color: #1f2937 !important;
              white-space: pre-line !important;
            }
            .content strong {
              display: block !important;
              font-size: 13pt !important;
              font-weight: bold !important;
              color: #2563eb !important;
              margin-top: 16px !important;
              margin-bottom: 4px !important;
              padding-bottom: 4px !important;
              border-bottom: 1px solid #dbeafe !important;
            }
            .practice-logo {
              max-height: 64px !important;
              max-width: 200px !important;
              margin-bottom: 10px !important;
            }
            .footer {
              margin-top: 28px !important;
              padding-top: 10px !important;
              border-top: 1px solid #cbd5e1 !important;
              font-size: 9pt !important;
              color: #64748b !important;
              text-align: center !important;
            }
            .footer p { margin: 2px 0 !important; }
            @media print {
              body { margin: 0.4in !important; }
              @page { margin: 0.4in !important; size: letter !important; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${practiceLogo ? `<img src="${practiceLogo}" alt="${practiceName}" class="practice-logo" />` : ''}
            <h1>${practiceName}</h1>
            ${practiceAddress ? `<p>${practiceAddress}</p>` : ''}
            <p>${practicePhone}${practiceWebsite ? ` | ${practiceWebsite}` : ''}</p>
          </div>
          
          <div class="procedure-title">${procedureName}</div>
          
          <div class="content">${content}</div>

          <div class="footer">
            <p>${practiceName}${practicePhone ? ' | ' + practicePhone : ''}${practiceWebsite ? ' | ' + practiceWebsite : ''}</p>
            <p>Printed: ${new Date().toLocaleDateString()}</p>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Modern Chrome no longer reliably fires onload for windows populated
      // via document.write(). Use the more reliable readyState polling +
      // setTimeout fallback so print() actually runs.
      const triggerPrint = () => {
        try {
          printWindow.focus();
          printWindow.print();
          // Close shortly after the user dismisses the print dialog
          setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
          console.log('✅ Print cleanup completed');
        } catch (e) {
          console.error('❌ Print trigger error:', e);
        }
      };

      // Single guarded trigger. 300ms delay lets Chrome parse the inline
      // <style> before print() fires (otherwise the dialog can open against
      // an unstyled DOM). The guard prevents the duplicate "ghost window"
      // flash that happened when both onload + setTimeout fired.
      let _printed = false;
      const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
      setTimeout(guardedPrint, 300);
      
      console.log('✅ Print initiated');
      
    } catch (error) {
      console.error('❌ Print error:', error);
      toast({
        title: "Error",
        description: `Failed to print: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  // SMS Functions
  const handleTextProcedure = (procedure) => {
    setSmsProcedure(procedure);
    setSmsPatient(null);
    setSmsPatientSearchQuery('');
    setShowSmsModal(true);
  };

  const sendSMS = async () => {
    if (!smsPatient) {
      toast({
        title: "Patient Required",
        description: "Please select a patient to send the text",
        variant: "destructive",
      });
      return;
    }

    const phoneNumber = smsPatient.cellPhone || smsPatient.phone;
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "Selected patient does not have a phone number",
        variant: "destructive",
      });
      return;
    }

    try {
      setSendingSMS(true);
      const token = localStorage.getItem('dentalToken');
      const patientName = `${smsPatient.firstName} ${smsPatient.lastName}`;
      
      const response = await fetch(`${backendUrl}/api/sms/send-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: smsPatient.id,
          document_type: 'procedure-template',
          document_id: smsProcedure.id,
          phone_number: phoneNumber
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send SMS');
      }

      toast({
        title: "Text Sent!",
        description: `Procedure "${smsProcedure.name}" texted to ${patientName}`,
      });

      setShowSmsModal(false);
      setSmsProcedure(null);
      setSmsPatient(null);
      setSmsPatientSearchQuery('');
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send text. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'Variable';
    return duration;
  };

  // Email Functions
  const handleEmailProcedure = (procedure) => {
    setEmailProcedure(procedure);
    setEmailPatient(null);
    setEmailPatientSearchQuery('');
    setShowEmailModal(true);
  };

  const sendEmailToPatient = async () => {
    if (!emailPatient) {
      toast({
        title: "Patient Required",
        description: "Please select a patient to email the procedure",
        variant: "destructive",
      });
      return;
    }
    if (!emailPatient.email) {
      toast({
        title: "No Email Address",
        description: "Selected patient does not have an email on file",
        variant: "destructive",
      });
      return;
    }

    try {
      setSendingEmail(true);
      const token = localStorage.getItem('dentalToken');
      const patientName = `${emailPatient.firstName} ${emailPatient.lastName}`;

      const response = await fetch(`${backendUrl}/api/email/send-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: emailPatient.id,
          document_type: 'procedure-template',
          document_id: emailProcedure.id,
        })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send email');
      }

      toast({
        title: "Email Sent!",
        description: `${emailProcedure.name} emailed to ${patientName}`,
      });

      setShowEmailModal(false);
      setEmailProcedure(null);
      setEmailPatient(null);
      setEmailPatientSearchQuery('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send email. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const getSpecialtyColor = (specialty) => {
    const colors = {
      'oral-surgery': 'bg-red-100 text-red-800',
      'general-dentistry': 'bg-blue-100 text-blue-800',
      'periodontics': 'bg-green-100 text-green-800',
      'endodontics': 'bg-purple-100 text-purple-800',
      'orthodontics': 'bg-pink-100 text-pink-800',
      'prosthodontics': 'bg-orange-100 text-orange-800',
    };
    return colors[specialty] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading procedures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/practice/dashboard')}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              {/* Test PDF button removed per user request */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Procedure Library</h1>
                <p className="text-gray-600">Browse and access post-operative care documents</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">{practice?.name}</p>
              <p className="text-xs text-gray-500">Practice Administrator</p>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Important:</strong> These are template documents. You must customize them for your practice and have them reviewed by your legal counsel before use.
          </AlertDescription>
        </Alert>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search procedures..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Procedures Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProcedures.map((procedure) => (
            <Card key={procedure.id} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{procedure.name}</CardTitle>
                  <Badge className={getSpecialtyColor(procedure.specialty)}>
                    {procedure.specialtyName || procedure.specialty}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Duration removed - not needed */}
                  
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {(procedure.description || procedure.purpose || procedure.overview || 'No description available').substring(0, 120)}...
                  </p>
                  
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => handleProcedureSelect(procedure)}
                      className="flex-1"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditProcedure(procedure)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadPDF(procedure)}
                      data-testid={`library-procedure-download-${procedure.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEmailProcedure(procedure)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`library-procedure-email-${procedure.id}`}
                      title="Email to patient"
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTextProcedure(procedure)}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      data-testid={`library-procedure-text-${procedure.id}`}
                      title="Text to patient"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePrintPDF(procedure)}
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProcedures.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No procedures found</h3>
            <p className="text-gray-600">
              {searchQuery ? 'Try adjusting your search terms' : 'No procedures available'}
            </p>
          </div>
        )}
      </div>

      {/* SMS Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              Text Procedure to Patient
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a patient to text <span className="font-medium text-blue-600">{smsProcedure?.name}</span>
            </p>
            
            {/* Patient Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name or email..."
                value={smsPatientSearchQuery}
                onChange={(e) => setSmsPatientSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Patient List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredSmsPatients.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Phone className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">
                    {smsPatientSearchQuery 
                      ? 'No patients with mobile numbers match your search' 
                      : 'No patients with mobile numbers available'}
                  </p>
                  <p className="text-xs mt-1">Only patients with phone numbers can receive text messages</p>
                </div>
              ) : (
                filteredSmsPatients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setSmsPatient(patient)}
                    className={`p-3 border rounded-lg transition cursor-pointer ${
                      smsPatient?.id === patient.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {patient.cellPhone || patient.phone}
                        </p>
                      </div>
                      {smsPatient?.id === patient.id && (
                        <Check className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSmsModal(false);
                  setSmsProcedure(null);
                  setSmsPatient(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={sendSMS}
                disabled={!smsPatient || sendingSMS}
                className="bg-green-600 hover:bg-green-700"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {sendingSMS ? 'Sending...' : 'Send Text'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Email Procedure to Patient
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a patient to email <span className="font-medium text-blue-600">{emailProcedure?.name}</span>
            </p>

            {/* Patient Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name or email..."
                value={emailPatientSearchQuery}
                onChange={(e) => setEmailPatientSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="library-email-patient-search"
              />
            </div>

            {/* Patient List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredEmailPatients.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Mail className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">
                    {emailPatientSearchQuery
                      ? 'No patients with email addresses match your search'
                      : 'No patients with email addresses available'}
                  </p>
                  <p className="text-xs mt-1">Only patients with email addresses on file can be emailed</p>
                </div>
              ) : (
                filteredEmailPatients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setEmailPatient(patient)}
                    className={`p-3 border rounded-lg transition cursor-pointer ${
                      emailPatient?.id === patient.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`library-email-patient-${patient.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {patient.email}
                        </p>
                      </div>
                      {emailPatient?.id === patient.id && (
                        <Check className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailProcedure(null);
                  setEmailPatient(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={sendEmailToPatient}
                disabled={!emailPatient || sendingEmail}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="library-email-send-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Procedure Preview Modal */}
      {showPreview && selectedProcedure && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedProcedure.name}
                  </h2>
                  <Badge className={getSpecialtyColor(selectedProcedure.specialty)}>
                    {selectedProcedure.specialtyName || selectedProcedure.specialty}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(false)}
                >
                  ×
                </Button>
              </div>
              
              <div className="space-y-4 pr-2">
                {/* Display content - handle BOTH legacy and Global Procedure formats */}
                <div className="text-gray-700 whitespace-pre-line leading-relaxed max-h-[60vh] overflow-y-auto">
                  {(() => {
                    // Only treat as "Global" (structured-sections format) if
                    // it has section fields with actual content. Section
                    // fields can be present as empty arrays — and empty
                    // arrays are truthy in JS — so check for length.
                    const _hasItems = (v) => {
                      if (!v) return false;
                      if (Array.isArray(v)) return v.some(i => i && String(i).trim());
                      if (typeof v === 'string') return v.trim().length > 0;
                      return true;
                    };
                    const isGlobal =
                      _hasItems(selectedProcedure.first24Hours) ||
                      _hasItems(selectedProcedure.painAndSwelling) ||
                      _hasItems(selectedProcedure.oralHygiene) ||
                      _hasItems(selectedProcedure.diet) ||
                      _hasItems(selectedProcedure.activity) ||
                      _hasItems(selectedProcedure.bleeding) ||
                      _hasItems(selectedProcedure.followUp) ||
                      _hasItems(selectedProcedure.specialPrecautions);

                    if (isGlobal) {
                      // Build display from structured fields
                      const sections = [];
                      
                      if (selectedProcedure.purpose) {
                        sections.push(<div key="purpose" className="mb-3"><strong className="text-gray-900">Purpose:</strong><br/>{selectedProcedure.purpose}</div>);
                      }
                      
                      if (selectedProcedure.first24Hours?.length > 0) {
                        const items = Array.isArray(selectedProcedure.first24Hours) ? selectedProcedure.first24Hours : [selectedProcedure.first24Hours];
                        sections.push(<div key="first24" className="mb-3"><strong className="text-gray-900">First 24 Hours:</strong><ul className="list-disc ml-5 mt-1">{items.filter(i=>i).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>);
                      }
                      
                      if (selectedProcedure.painAndSwelling?.length > 0) {
                        const items = Array.isArray(selectedProcedure.painAndSwelling) ? selectedProcedure.painAndSwelling : [selectedProcedure.painAndSwelling];
                        sections.push(<div key="pain" className="mb-3"><strong className="text-gray-900">Pain and Swelling:</strong><ul className="list-disc ml-5 mt-1">{items.filter(i=>i).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>);
                      }
                      
                      if (selectedProcedure.oralHygiene?.length > 0) {
                        const items = Array.isArray(selectedProcedure.oralHygiene) ? selectedProcedure.oralHygiene : [selectedProcedure.oralHygiene];
                        sections.push(<div key="hygiene" className="mb-3"><strong className="text-gray-900">Oral Hygiene:</strong><ul className="list-disc ml-5 mt-1">{items.filter(i=>i).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>);
                      }
                      
                      if (selectedProcedure.diet?.length > 0) {
                        const items = Array.isArray(selectedProcedure.diet) ? selectedProcedure.diet : [selectedProcedure.diet];
                        sections.push(<div key="diet" className="mb-3"><strong className="text-gray-900">Diet:</strong><ul className="list-disc ml-5 mt-1">{items.filter(i=>i).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>);
                      }
                      
                      if (selectedProcedure.activity?.length > 0) {
                        const items = Array.isArray(selectedProcedure.activity) ? selectedProcedure.activity : [selectedProcedure.activity];
                        sections.push(<div key="activity" className="mb-3"><strong className="text-gray-900">Activity:</strong><ul className="list-disc ml-5 mt-1">{items.filter(i=>i).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>);
                      }
                      
                      if (selectedProcedure.specialPrecautions?.length > 0) {
                        const items = Array.isArray(selectedProcedure.specialPrecautions) ? selectedProcedure.specialPrecautions : [selectedProcedure.specialPrecautions];
                        sections.push(<div key="precautions" className="mb-3"><strong className="text-gray-900">Special Precautions:</strong><ul className="list-disc ml-5 mt-1">{items.filter(i=>i).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>);
                      }
                      
                      if (selectedProcedure.followUp?.length > 0) {
                        const items = Array.isArray(selectedProcedure.followUp) ? selectedProcedure.followUp : [selectedProcedure.followUp];
                        sections.push(<div key="followup" className="mb-3"><strong className="text-gray-900">Follow Up:</strong><ul className="list-disc ml-5 mt-1">{items.filter(i=>i).map((item, idx) => <li key={idx}>{item}</li>)}</ul></div>);
                      }
                      
                      return sections.length > 0 ? sections : 'No content available';
                    }
                    
                    // Legacy format
                    return selectedProcedure.overview || selectedProcedure.fullText || 'No content available';
                  })()}
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <Button
                  onClick={() => handleDownloadPDF(selectedProcedure)}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => handlePrintPDF(selectedProcedure)}
                  variant="outline"
                  className="flex-1"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeLibraryPage;